/**
 * How many entries a chart's legend will show — the basis for auto-hiding a
 * legend that would carry a single, redundant entry (see ChartRenderer).
 *
 * Type-agnostic, driven by the normalized model, not a concrete-type check:
 * - explicit series (multiSeries charts: line/bar/distribution/box) → one entry
 *   per series;
 * - otherwise a grouped chart (groupKey, e.g. scatter/pie/spatialCategorical) →
 *   one entry per distinct category in the data;
 * - otherwise a single series (a lone yKey, an ungrouped cloud, a heatmap) → 1.
 *
 * This counts *legend entries*, not Plotly traces, on purpose: a pie emits one
 * trace but a legend entry per slice, so a raw trace count would undercount it.
 */
import type { NormalizedChartModel } from '../model';
import { uniqueValues } from './uniqueValues';
import { measureTextWidth, type FontSpec } from './text';

export function countLegendEntries(model: NormalizedChartModel): number {
  if (model.series.length > 0) return model.series.length;
  const groupKey = model.encoding.groupKey;
  // Counted over the whole dataset, matching the category set grouped charts build
  // from (see spatialCategorical): a filtered-away category still holds its legend
  // entry, and this estimate feeds the reserved legend height — if the two disagreed,
  // the space reserved would not be the space used.
  if (groupKey) return uniqueValues(model.unfilteredData ?? model.data, groupKey).length || 1;
  return 1;
}

/**
 * Height in px the horizontal legend below the plot will take.
 *
 * Needed because that legend is anchored to the CONTAINER's bottom and Plotly's
 * `autoexpand` grows the bottom margin to fit it — so the plot area is shorter
 * than `layout.margin.b` says, and anything sized against the authored margin
 * (the stats column) budgets for space the legend has already taken. Measured on
 * the scatter: a two-row legend, and the TRANSICIONES block printed straight
 * through it.
 *
 * Entries are packed greedily into the figure width, exactly as Plotly wraps
 * them. `itemWidth` covers the swatch plus the gap to the next entry; it scales
 * with the font like everything else on the row.
 */
export function estimateLegendHeight(
  entryLabels: string[],
  figureWidth: number,
  font: FontSpec,
  /**
   * The legend's own title, when it has one (`labels.legendHeading`). Plotly draws it on
   * its own line ABOVE the entries and does not wrap it, so it is one more line of
   * height that a caller budgeting against the legend has to know about. Omitted by
   * callers whose legend carries no title.
   */
  title?: string
): number {
  if (entryLabels.length === 0 || figureWidth <= 0) return 0;
  const titleRows = title ? 1 : 0;
  const itemWidth = LEGEND_ITEM_SWATCH * (font.size / LEGEND_REFERENCE_FONT);
  let rows = 1;
  let used = 0;
  for (const label of entryLabels) {
    const w = measureTextWidth(label, font) + itemWidth;
    if (used > 0 && used + w > figureWidth) {
      rows += 1;
      used = w;
    } else {
      used += w;
    }
  }
  return (rows + titleRows) * font.size * LEGEND_LINE_HEIGHT + LEGEND_VERTICAL_PAD;
}

/** Swatch + inter-entry gap (px) Plotly draws per legend entry, at the base font. */
const LEGEND_ITEM_SWATCH = 44;
/** Font size the swatch width above was measured at. */
const LEGEND_REFERENCE_FONT = 12.5;
/** Plotly's LINE_SPACING, the same constant the title and stats stacks use. */
const LEGEND_LINE_HEIGHT = 1.3;
/** Padding Plotly leaves above and below the legend box. */
const LEGEND_VERTICAL_PAD = 12;
